import { Button } from "@/components/ui/button";
import { VENUE_AUDIT_KIND_LABEL } from "@/lib/tina/venue-audit";
import type { VenueAuditKindCounts } from "@/pages/tina/audits/venue/hooks/use-venue-audit-repository";
import { VENUE_AUDIT_KINDS, type VenueAuditKind } from "@/types/tina";

interface KindCountStripProps {
  counts: VenueAuditKindCounts | undefined;
  isLoading: boolean;
  selected: VenueAuditKind | null;
  onSelect: (kind: VenueAuditKind | null) => void;
}

/**
 * The shape of the backlog, as nine clickable counts.
 *
 * This exists because of how lopsided the real backlog is: the first one is ~178 findings
 * of which ~123 are a single kind, `non_venue_type`, and 36 are low-severity CID
 * collisions. A pager over a mixed 178 rows tells a curator nothing about what they are
 * in for and offers no way to do one kind at a time; nine counts do both, and each is one
 * click into a queue of a single question.
 *
 * The counts come from nine `limit=1` reads of the same route the table pages, so each is
 * the server's own count over the same WHERE — never a tally of the loaded page, which
 * would cap every number at the page size.
 *
 * A count of zero still renders, greyed: "this kind is clear" is information, and a
 * strip whose buttons come and go cannot be clicked reliably.
 */
export function KindCountStrip({
  counts,
  isLoading,
  selected,
  onSelect,
}: KindCountStripProps) {
  const total = counts
    ? VENUE_AUDIT_KINDS.reduce((sum, kind) => sum + (counts[kind] ?? 0), 0)
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant={selected === null ? "default" : "outline"}
        size="sm"
        className="h-7 text-xs"
        onClick={() => onSelect(null)}
      >
        Everything
        <span className="ml-2 font-mono tabular-nums">
          {isLoading || total === null ? "…" : total}
        </span>
      </Button>
      {VENUE_AUDIT_KINDS.map((kind) => {
        const count = counts?.[kind];
        return (
          <Button
            key={kind}
            variant={selected === kind ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => onSelect(selected === kind ? null : kind)}
          >
            <span className={count === 0 ? "text-muted-foreground" : undefined}>
              {VENUE_AUDIT_KIND_LABEL[kind]}
            </span>
            <span className="ml-2 font-mono tabular-nums">
              {isLoading || count === undefined ? "…" : count}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
